// react
import { useCallback, useEffect, useState } from 'react';

// services
import {
	FetchParams,
	fetchAccounts,
	fetchContracts,
	fetchEntities,
	fetchPlanningActivities,
	fetchSpendPlans,
	fetchWorkPlans,
} from '../services/api';

// types
import type { LookupItem } from '../components/dialogs/DialogBulkUpload';

// Project-universe lookup lists for bulk-upload relation resolution.
//
// The resolve-relationship dropdowns list whatever the API returns for the
// project, already permission-filtered server-side. Every list is fetched
// without a page limit — the backend returns every row when limit/page are
// omitted — so the pickers show the full project set rather than the single
// paginated page held by the listing contexts. Only the datasets a page opts
// into are fetched, and only while the caller is enabled (e.g. the upload
// dialog is open).

export interface UseProjectLookupsOptions {
	projectId?: number;
	isEnabled?: boolean;
	/** Comma-separated entity types to load into `entities` (e.g. 'PIU,SUB_PIU'). */
	entityTypes?: string;
	accounts?: boolean;
	activities?: boolean;
	contracts?: boolean;
	spendPlans?: boolean;
	suppliers?: boolean;
	workPlans?: boolean;
}

export interface ProjectLookups {
	accounts: LookupItem[];
	activities: LookupItem[];
	contracts: LookupItem[];
	entities: LookupItem[];
	spendPlans: LookupItem[];
	suppliers: LookupItem[];
	workPlans: LookupItem[];
	refetch: () => Promise<void>;
}

export const useProjectLookups = ({
	projectId,
	isEnabled = true,
	entityTypes,
	accounts = false,
	activities = false,
	contracts = false,
	spendPlans = false,
	suppliers = false,
	workPlans = false,
}: UseProjectLookupsOptions): ProjectLookups => {
	const [entitiesData, setEntitiesData] = useState<LookupItem[]>([]);
	const [accountsData, setAccountsData] = useState<LookupItem[]>([]);
	const [activitiesData, setActivitiesData] = useState<LookupItem[]>([]);
	const [contractsData, setContractsData] = useState<LookupItem[]>([]);
	const [spendPlansData, setSpendPlansData] = useState<LookupItem[]>([]);
	const [suppliersData, setSuppliersData] = useState<LookupItem[]>([]);
	const [workPlansData, setWorkPlansData] = useState<LookupItem[]>([]);

	const load = useCallback(async () => {
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		if (!projectId || !accessToken || !idToken) return;
		const auth: FetchParams = { accessToken, idToken };

		try {
			await Promise.all([
				entityTypes
					? fetchEntities(auth, { project: projectId, type: entityTypes }).then((res) => {
							if (res.data) {
								setEntitiesData(
									res.data.map((e) => ({
										id: e.id,
										name: e.name || String(e.id),
										idExternal: e.idExternal,
									}))
								);
							}
						})
					: Promise.resolve(),
				suppliers
					? fetchEntities(auth, { project: projectId, type: 'SUPPLIER' }).then((res) => {
							if (res.data) {
								setSuppliersData(
									res.data.map((s) => ({
										id: s.id,
										name: s.name || String(s.id),
										idExternal: s.idExternal,
									}))
								);
							}
						})
					: Promise.resolve(),
				accounts
					? fetchAccounts(auth, { project: projectId, status: 'ACTIVE' }).then((res) => {
							if (res.data) {
								setAccountsData(
									res.data.map((a) => ({
										id: a.id,
										name: a.name || a.institution || String(a.id),
									}))
								);
							}
						})
					: Promise.resolve(),
				activities
					? fetchPlanningActivities(auth, { project: projectId }).then((res) => {
							if (res.data) {
								setActivitiesData(
									res.data.map((a) => ({ id: a.id, name: a.name || String(a.id) }))
								);
							}
						})
					: Promise.resolve(),
				contracts
					? fetchContracts(auth, { project: projectId }).then((res) => {
							if (res.data) {
								setContractsData(
									res.data.map((c) => ({
										id: c.id,
										name: c.description || c.num || String(c.id),
									}))
								);
							}
						})
					: Promise.resolve(),
				spendPlans
					? fetchSpendPlans(auth, { project: projectId, status: 'APPROVED' }).then((res) => {
							if (res.data) {
								setSpendPlansData(
									res.data.map((sp) => ({ id: sp.id, name: sp.name || String(sp.id) }))
								);
							}
						})
					: Promise.resolve(),
				workPlans
					? fetchWorkPlans(auth, { project: projectId }).then((res) => {
							if (res.data) {
								setWorkPlansData(
									res.data.map((wp) => ({ id: wp.id, name: wp.name || String(wp.id) }))
								);
							}
						})
					: Promise.resolve(),
			]);
		} catch {
			/* ignore — lookup data is best-effort */
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		projectId,
		isEnabled,
		entityTypes,
		accounts,
		activities,
		contracts,
		spendPlans,
		suppliers,
		workPlans,
	]);

	useEffect(() => {
		if (isEnabled) void load();
	}, [isEnabled, load]);

	return {
		accounts: accountsData,
		activities: activitiesData,
		contracts: contractsData,
		entities: entitiesData,
		spendPlans: spendPlansData,
		suppliers: suppliersData,
		workPlans: workPlansData,
		refetch: load,
	};
};
